import ClearIcon from "@mui/icons-material/Clear";
import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Modal,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Input from "components/Input/Input";
import { debounce } from "lodash";
import React, { useState } from "react";
import { get } from "services/axios";
import { useNavigate } from "react-router-dom";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

interface RawQueryI {
  articles: any[];
  tags: any[];
  users: any[];
}

const SearchModal = ({ open, onClose }: SearchModalProps) => {
  const [value, setValue] = useState<number>(0);
  const [rawQuery, setRawQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const initialQueryData = {
    articles: [],
    tags: [],
    users: [],
  };
  const [rawQueryData, setRawQueryData] = useState<RawQueryI>(initialQueryData);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const delayedSearch = debounce((searchedValue?: string) => {
    handleSearch(searchedValue);
  }, 500);

  const handleInputChange = (e: any) => {
    setRawQuery(e.target.value);
    delayedSearch(e.target.value);
  };

  const handleSearch = async (searchedValue?: string) => {
    let value = searchedValue || rawQuery;
    try {
      setLoading(true);
      const response = await get(
        "/feed/search",
        null,
        {},
        {
          params: { rawQuery: value },
        }
      );
      if (response.status === 200) {
        setRawQueryData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setRawQueryData(initialQueryData);
      console.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const renderAccounts = () => (
    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
      <List>
        {rawQueryData.users.length > 0
          ? rawQueryData.users.map((account) => (
              <React.Fragment key={account.id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar src={account.profileImage} />
                  </ListItemAvatar>
                  <ListItemText
                    onClick={() => {
                      onClose();
                      navigate(`/author/${account?.userName}`, {
                        state: {
                          userId: account?._id,
                          username: account?.userName,
                        },
                      });
                    }}
                    primary={account.name}
                    secondary={account.bio}
                  />
                  <IconButton edge="end">
                    <ClearIcon />
                  </IconButton>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))
          : rawQuery.trim() !== "" && (
              <Typography
                variant="body2"
                color="textSecondary"
                align="center"
                sx={{ mt: 2 }}
              >
                No Records Found
              </Typography>
            )}
      </List>
    </Box>
  );

  const ArticleList = () => (
    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
      <List>
        {rawQueryData.articles.length > 0
          ? rawQueryData.articles.map((article) => (
              <React.Fragment key={article.article_id}>
                <ListItem
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    handleArticlePageNavigation(
                      article.title,
                      article.article_id,
                      article.author_details?.userName
                    )
                  }
                >
                  <ListItemText primary={article.title} />
                  <IconButton edge="end">
                    <ClearIcon />
                  </IconButton>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))
          : rawQuery.trim() !== "" && (
              <Typography
                variant="body2"
                color="textSecondary"
                align="center"
                sx={{ mt: 2 }}
              >
                No Records Found
              </Typography>
            )}
      </List>
    </Box>
  );

  const TagList = () => {
    return (
      <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
        <List>
          {rawQueryData.tags.length > 0
            ? rawQueryData.tags.map((tag) => (
                <React.Fragment key={tag._id}>
                  <ListItem>
                    <ListItemText
                      style={{ textTransform: "capitalize" }}
                      primary={tag.name}
                      secondary={`${tag.followers_count} Followers | ${tag.published_stories_count} Stories`}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))
            : rawQuery.trim() !== "" && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mt: 2 }}
                >
                  No Records Found
                </Typography>
              )}
        </List>
      </Box>
    );
  };

  const handleArticlePageNavigation = (
    articleTitle: string,
    articleId: string,
    userName: any
  ) => {
    onClose();
    navigate(`/article/${userName}/${articleTitle}`, {
      state: { userName: userName, articleId },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Container
        maxWidth="sm"
        style={{
          marginTop: "10%",
          borderRadius: "8px",
        }}
      >
        <Box p={2}>
          <div>
            <Input
              name="search"
              autoFocus={true}
              className="w-[250px] mb-5"
              onChange={handleInputChange}
              placeholder="Search Anything..."
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            {rawQuery.trim() !== "" && (
              <p>
                Results for : <b className="text-[18px]">{rawQuery}</b>
              </p>
            )}
          </div>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="fullWidth"
            className="mt-3"
            sx={{ width: "fit-content" }}
          >
            <Tab label="Articles" />
            <Tab label="Users" />
            <Tab label="Tags" />
          </Tabs>
          {loading ? (
            <Box textAlign="center" m={2}>
              <CircularProgress />
            </Box>
          ) : value === 0 ? (
            <ArticleList />
          ) : value === 1 ? (
            renderAccounts()
          ) : value === 2 ? (
            <TagList />
          ) : null}
        </Box>
      </Container>
    </Modal>
  );
};

export default SearchModal;
